import React, {
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  pluralize,
} from '@populateui/utils';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  AppointmentTypeSettings,
} from 'views/EMR/Settings/Appointments/types';
import {
  useGetSettingsGetAppointmentSettings,
  useUpsertAppointmentType,
} from 'views/EMR/Settings/hooks';
import keyBy from 'lodash/keyBy';
import EditIcon from 'core/icons/edit';
import Popover from 'core/components/Popover';
import IntakeQustionnaireType from 'views/EMR/Settings/Appointments/IntakeQustionnaireType';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Spinner from 'pages/Dashboard/components/Spinner';
import isNil from 'lodash/isNil';

type TypeColumnDialogProps = {
  original: AppointmentTypeSettings;
};

function TypeColumnDialog({ original }: TypeColumnDialogProps) {
  const { intakeQuestionnaireType } = original;
  const {
    data: { intakeQuestionnaireOptions = [] } = {},
  } = useGetSettingsGetAppointmentSettings();

  const intakeQuestionnaireTypeByValue = useMemo(
    () => keyBy(intakeQuestionnaireOptions, 'value'),
    [intakeQuestionnaireOptions],
  );

  const intakeQuestionnaireOptionByType = intakeQuestionnaireTypeByValue[
    intakeQuestionnaireType as keyof typeof intakeQuestionnaireTypeByValue
  ];

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { mutateAsync: upsertAppointmentType, isPending } = useUpsertAppointmentType();

  const onUpdate = async (value: string) => {
    try {
      setAnchorEl(null);
      await upsertAppointmentType({
        ...original,
        intakeQuestionnaireType: value,
      });
      settingsEventChannel.emit('onAppointmentTypesChange');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <div
        role="button"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setAnchorEl(e.currentTarget as HTMLDivElement);
          }
        }}
        className="z-50 flex items-center gap-1 hover:text-blue-600 p-1 -m-1"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget as HTMLDivElement);
        }}
      >
        {isPending
          ? <Spinner className="!w-4 !h-4" /> : (
            <>
              <span>{intakeQuestionnaireOptionByType?.name ?? '--'}</span>
              <EditIcon className="!w-5 !h-5 text-inherit" />
            </>
          )}
      </div>

      {!isNil(anchorEl) && (
      <Popover
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        classes={{ paper: '!w-[520px]' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <IntakeQustionnaireType
          options={intakeQuestionnaireOptions ?? []}
          onClose={() => {
            setAnchorEl(null);
          }}
          onSameValueSelected={() => {
            setAnchorEl(null);
          }}
          value={intakeQuestionnaireType ?? ''}
          onChange={(e) => {
            onUpdate(e.target.value);
          }}
        />
      </Popover>
      )}
    </>
  );
}

const appointmentTypeColumns = [{
  id: 'appointmentTypeName',
  accessorKey: 'appointmentTypeName',
  columnTitle: 'Type of Visit',
  size: 300,
  cell: ({ row: { original: { appointmentTypeName, color } } }) => (
    <div className="flex gap-2 items-center">
      <div className={clsx('w-2.5 h-2.5 rounded-full', color)} />
      <div className="w-fit">{appointmentTypeName}</div>
    </div>
  ),
},
{
  id: 'type',
  accessorKey: 'type',
  columnTitle: 'Intake Questionnaire Type',
  size: 300,
  cell: ({ row: { original } }) => (<TypeColumnDialog original={original} />),
}, {
  id: 'defaultDurationInMinutes',
  accessorKey: 'defaultDurationInMinutes',
  columnTitle: 'Duration',
  meta: { autoSized: true },
  cell: ({ row: { original: { defaultDurationInMinutes } } }) => pluralize(
    'minute',
    defaultDurationInMinutes,
  ),
}] as ColumnDef<AppointmentTypeSettings>[];

export default appointmentTypeColumns;
